import { i18nMark } from 'lingui-react';
import { px2rem } from 'decisiv-ui-utils';

import buildEnum from 'utils/buildEnum';
import { reportTypes } from 'pages/Reports/constants';
import { CASE_STATUS } from 'compositions/CaseStatus';

const buildOptions = (types, titles) =>
  Object.entries(types)
    .reduce((acc, [, value]) => [...acc, [value, titles[value]]], [])
    .filter(Boolean);

export const dropdownContentStyles = {
  maxHeight: px2rem(400),
  overflowY: 'auto',
  right: 'auto',
  whiteSpace: 'nowrap',
};

const dailyDealerReportTitle = i18nMark(
  'Enter your criteria below to run a report on all the case activity per service provider over a 24-hour period (start time is 9 AM).',
);

export const instructionsByType = {
  [reportTypes.caseBillingReport]: i18nMark(
    'Enter your criteria below to run a report on all the billing activity over a 24-hour period (start time is 12 AM).',
  ),
  [reportTypes.casesPerHour]: i18nMark(
    'Enter your criteria below to run a report on the total number of cases created per hour over a 24-hour period (start time is 12 AM).',
  ),
  [reportTypes.dailyAgentMetrics]: i18nMark(
    'Enter your criteria below to run a report on all the cases created, dispatched, and rolled per agent over a 24-hour period (start time is 12 AM).',
  ),
  [reportTypes.dailyCasesMetrics]: i18nMark(
    'Enter your criteria below to run a report on all the case activity per hour over a 24-hour period (start time is 12 AM).',
  ),
  [reportTypes.dailyDealerReport]: dailyDealerReportTitle,
  [reportTypes.dailyDealerReportByEmail]: dailyDealerReportTitle,

  [reportTypes.assetValidationReport]: i18nMark(
    'Enter your criteria below to run a report on the summary or breakdown of asset validation attempts by inbound program, and over a period of time.',
  ),
};

export const configTypes = buildEnum([
  'reportType',
  'reportingPeriod',
  'customReportingPeriod',
  'billableStatus',
  'caseNumber',
  'unitNumber',
  'customerName',
  'customerLocation',
  'inboundProgramName',
  'dealerName',
  'poNumber',
  'paymentMethod',
  'status',
  'exportType',
  'dateRangeType',
  'reportLevel',
  'recipientAddresses',
  'detailLevel',
  'inboundProgramIds',
  'inboundProgramSelectionType',
]);

export const configTypesTitles = {
  [configTypes.reportType]: i18nMark('Report'),
  [configTypes.dateRangeType]: i18nMark('Filter By'),
  [configTypes.reportingPeriod]: i18nMark('Reporting Period'),
  [configTypes.billableStatus]: i18nMark('Billable/Not Billable'),
  [configTypes.caseNumber]: i18nMark('Case Number'),
  [configTypes.unitNumber]: i18nMark('Unit Number'),
  [configTypes.customerName]: i18nMark('Fleet Name'),
  [configTypes.customerLocation]: i18nMark('Fleet Location'),
  [configTypes.inboundProgramName]: i18nMark('Inbound Program'),
  [configTypes.dealerName]: i18nMark('Service Provider'),
  [configTypes.poNumber]: i18nMark('PO Number'),
  [configTypes.paymentMethod]: i18nMark('Payment Type'),
  [configTypes.status]: i18nMark('Status'),
  [configTypes.exportType]: i18nMark('File Format'),
  [configTypes.recipientAddresses]: i18nMark(
    'Send to Email Addresses (comma separated)',
  ),
};

export const configTypesModalTitles = {
  [configTypes.recipientAddresses]: i18nMark('Recipient Addresses'),
  [configTypes.detailLevel]: i18nMark('Level of Detail'),
  [configTypes.inboundProgramIds]: i18nMark('Inbound Programs'),
};

export const configTypesPlaceholders = {
  [configTypes.caseNumber]: i18nMark('Search by case number...'),
  [configTypes.unitNumber]: i18nMark('Search by unit number...'),
  [configTypes.customerName]: i18nMark('Search by fleet name...'),
  [configTypes.customerLocation]: i18nMark('Enter fleet location...'),
  [configTypes.inboundProgramName]: i18nMark('Search by program name...'),
  [configTypes.dealerName]: i18nMark('Search by service provider name...'),
  [configTypes.poNumber]: i18nMark('Search by PO number...'),
  [configTypes.paymentMethod]: i18nMark('Select payment type...'),
  [configTypes.status]: i18nMark('Select status...'),
  [configTypes.recipientAddresses]: i18nMark('Enter email addresses...'),
};

export const configTypesWarnings = {
  [configTypes.caseNumber]: i18nMark('Select an existing case.'),
  [configTypes.unitNumber]: i18nMark('Select an existing unit.'),
  [configTypes.customerName]: i18nMark('Select an existing fleet.'),
  [configTypes.inboundProgramName]: i18nMark('Select an existing program.'),
  [configTypes.dealerName]: i18nMark('Select an existing service provider.'),
  [configTypes.poNumber]: i18nMark('Select an existing PO.'),
};

const dailyDealerConfigVars = [
  configTypes.caseNumber,
  configTypes.customerLocation,
  configTypes.customerName,
  configTypes.dealerName,
  configTypes.inboundProgramName,
  configTypes.paymentMethod,
  configTypes.poNumber,
  configTypes.unitNumber,
  configTypes.exportType,
  configTypes.dateRangeType,
  configTypes.reportLevel,
  configTypes.recipientAddresses,
];

export const customConfigVarsByType = {
  [reportTypes.caseBillingReport]: [
    configTypes.dealerName,
    configTypes.inboundProgramName,
  ],
  [reportTypes.dailyCasesMetrics]: [
    configTypes.caseNumber,
    configTypes.customerLocation,
    configTypes.customerName,
    configTypes.dealerName,
    configTypes.inboundProgramName,
    configTypes.poNumber,
    configTypes.status,
    configTypes.unitNumber,
  ],
  [reportTypes.dailyDealerReport]: dailyDealerConfigVars,
  [reportTypes.dailyDealerReportByEmail]: dailyDealerConfigVars,
  [reportTypes.assetValidationReport]: [
    configTypes.detailLevel,
    configTypes.inboundProgramIds,
  ],
};

// The order of the types defines the order of the dropdown options
export const reportingPeriodTypes = buildEnum([
  'today',
  'yesterday',
  'last7days',
  'last30days',
  'last90days',
  'custom',
]);

export const reportingPeriodTitles = {
  [reportingPeriodTypes.today]: i18nMark('Today'),
  [reportingPeriodTypes.yesterday]: i18nMark('Yesterday'),
  [reportingPeriodTypes.last7days]: i18nMark('Last 7 Days'),
  [reportingPeriodTypes.last30days]: i18nMark('Last 30 Days'),
  [reportingPeriodTypes.last90days]: i18nMark('Last 90 Days'),
  [reportingPeriodTypes.custom]: i18nMark('Custom'),
};

export const reportingPeriodOptions = buildOptions(
  reportingPeriodTypes,
  reportingPeriodTitles,
);

// The order of the types defines the order of the dropdown options
export const billableStatusTypes = buildEnum([
  'any',
  'billable',
  'notBillable',
]);

export const billableStatusTitles = {
  [billableStatusTypes.any]: i18nMark('All'),
  [billableStatusTypes.billable]: i18nMark('Billable'),
  [billableStatusTypes.notBillable]: i18nMark('Not Billable'),
};

export const billableStatusOptions = buildOptions(
  billableStatusTypes,
  billableStatusTitles,
);

export const statusTitles = {
  [CASE_STATUS.new]: i18nMark('Case Creation'),
  [CASE_STATUS.dispatch]: i18nMark('To Dispatch'),
  [CASE_STATUS.dispatched]: i18nMark('Dispatched'),
  [CASE_STATUS.rolling]: i18nMark('Vehicle Rolling'),
  [CASE_STATUS.canceled]: i18nMark('Canceled'),
  [CASE_STATUS.closed]: i18nMark('Closed'),
};

// The order of the types defines the order of the dropdown options
export const paymentMethodTypes = buildEnum([
  'cash',
  'none',
  'comCheck',
  'cardOnFile',
  'oneTimeCard',
  'creditCardNoPreauth',
  'thirdPartyCreditCardTxn',
  'michelinLineOfCredit',
  'otherNationalAccount',
  'storeAccount',
]);

export const paymentMethodTitles = {
  [paymentMethodTypes.cash]: i18nMark('Cash'),
  [paymentMethodTypes.none]: i18nMark('Continue without payment method'),
  [paymentMethodTypes.comCheck]: i18nMark('COMcheck'),
  [paymentMethodTypes.cardOnFile]: i18nMark('Credit Card - Existing '),
  [paymentMethodTypes.oneTimeCard]: i18nMark('Credit Card - New'),
  [paymentMethodTypes.creditCardNoPreauth]: i18nMark(
    'Credit Card - No Preauth',
  ),
  [paymentMethodTypes.thirdPartyCreditCardTxn]: i18nMark(
    'Credit Card - Transaction Receipt',
  ),
  [paymentMethodTypes.michelinLineOfCredit]: i18nMark('National Account'),
  [paymentMethodTypes.otherNationalAccount]: i18nMark('Other National Account'),
  [paymentMethodTypes.storeAccount]: i18nMark('Store Account'),
};

export const paymentMethodTypesOptions = buildOptions(
  paymentMethodTypes,
  paymentMethodTitles,
);

export const exportTypes = buildEnum(['csv', 'pdf']);

export const exportTypesTitles = {
  [exportTypes.csv]: i18nMark('CSV'),
  [exportTypes.pdf]: i18nMark('PDF'),
};

export const dateRangeTypes = buildEnum(['creationTime', 'rollTime']);

export const dateRangeTypesTitles = {
  [dateRangeTypes.creationTime]: i18nMark('Filter by creation date'),
  [dateRangeTypes.rollTime]: i18nMark('Filter by rolling date'),
};

export const dateRangeTypesModalTitles = {
  [dateRangeTypes.creationTime]: i18nMark('Creation date'),
  [dateRangeTypes.rollTime]: i18nMark('Rolling date'),
};

export const defaultReportConfig = {
  [configTypes.dateRangeType]: dateRangeTypes.creationTime,
  [configTypes.customReportingPeriod]: undefined,
  [configTypes.exportType]: exportTypes.csv,
  [configTypes.reportingPeriod]: reportingPeriodTypes.today,
  [configTypes.billableStatus]: billableStatusTypes.any,
};

export const createReportJobStatusTypes = buildEnum([
  'loading',
  'failure',
  'success',
]);

export const reportStatusTypes = buildEnum(['loading', 'failure', 'success']);

export const reportLevels = buildEnum(['billTo', 'shipTo']);

export const defaultReportLevel = reportLevels.shipTo;

export const configValuesTransformers = {
  [reportTypes.assetValidationReport]: {
    [configTypes.inboundProgramIds]: ids => ids.map(id => parseInt(id, 10)),
  },
};



// WEBPACK FOOTER //
// ./src/compositions/Reports/ReportConfigBuilder/constants.js